import React from "react";
import { useTranslation } from "react-i18next";
import CarrierLogo from "../CarrierLogo";
import CarrierToggle from "../CarrierToggle";
import Checkbox from "../Checkbox";
import ServicesPill from "../ServicesPill";
import ServiceChecklist from "./ServiceChecklist";
import { carrierLogo, carrierName, isCarrierOn, serviceEntries } from "../../carrierHelpers";

const HEAD_CELL = "border-b border-ui-border px-3.5 py-2.5 text-left text-[11px] font-medium text-ui-text-muted";
const BULK_BUTTON = "h-7 rounded-md border border-state-info-border bg-ui-surface px-3 text-xs font-medium text-brand disabled:cursor-wait disabled:opacity-60";

const CarrierListView = ({
  rows,
  expanded,
  onExpand,
  selected,
  onSelect,
  onSelectAll,
  onClearSelection,
  busy,
  onToggle,
  onServicesChange,
  onBulkEnable,
  // onBulkServicesOn, // Disabled until the bulk services API exists.
}) => {
  const { t } = useTranslation("settings");
  const selectedCount = rows.filter((row) => selected[row.id]).length;
  const bulkBusy = rows.some((row) => selected[row.id] && busy[row.id]);
  const headState =
    selectedCount === 0 ? false : selectedCount === rows.length ? true : "mixed";

  return (
    <div className="overflow-hidden rounded-xl border border-ui-border bg-ui-surface">
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-state-info-border bg-brand-light px-3.5 py-2 text-xs text-brand">
          <strong className="font-semibold">
            {t("carriers.selected", { count: selectedCount })}
          </strong>
          <button
            type="button"
            onClick={() => onBulkEnable(true)}
            disabled={bulkBusy}
            className="h-7 rounded-md bg-brand px-3 text-xs font-medium text-brand-contrast disabled:cursor-wait disabled:opacity-60"
          >
            {t("carriers.enableCarriers")}
          </button>
          <button
            type="button"
            onClick={() => onBulkEnable(false)}
            disabled={bulkBusy}
            className={BULK_BUTTON}
          >
            {t("carriers.disableCarriers")}
          </button>
          {/* Disabled until the bulk services API exists: one request per service was too slow.
          <button type="button" onClick={onBulkServicesOn} className={BULK_BUTTON}>
            Turn on all services
          </button> */}
          <button
            type="button"
            onClick={onClearSelection}
            className="ml-auto h-7 rounded-md px-2.5 text-xs text-brand"
          >
            {t("common:actions.clear")}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="bg-ui-surface-sunken">
              <th className="w-11 border-b border-ui-border py-2.5 pl-3.5">
                <Checkbox checked={headState} onChange={onSelectAll} label={t("carriers.selectAll")} />
              </th>
              <th className={HEAD_CELL}>{t("carriers.columns.carrier")}</th>
              <th className={HEAD_CELL}>{t("carriers.columns.services")}</th>
              <th className={HEAD_CELL}>{t("carriers.columns.inQuotes")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const name = carrierName(row);
              const on = isCarrierOn(row);
              const isExpanded = expanded[row.id] && serviceEntries(row).length > 0;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`border-t border-ui-border-subtle ${
                      selected[row.id] ? "bg-ui-row-selected" : "bg-ui-surface"
                    }`}
                  >
                    <td className="py-2 pl-3.5">
                      <Checkbox
                        checked={!!selected[row.id]}
                        onChange={() => onSelect(row.id)}
                        label={t("carriers.selectOne", { name })}
                      />
                    </td>
                    <td className="px-3.5 py-2">
                      <div className="flex items-center gap-2.5">
                        <CarrierLogo src={carrierLogo(row)} name={name} size="sm" dimmed={!on} />
                        <span className="text-[13px] font-semibold text-ui-text">{name}</span>
                      </div>
                    </td>
                    <td className="px-3.5 py-2">
                      <ServicesPill
                        row={row}
                        expanded={isExpanded}
                        onToggle={() => onExpand(row.id)}
                      />
                    </td>
                    <td className="px-2">
                      <CarrierToggle
                        checked={on}
                        onChange={(next) => onToggle(row, next)}
                        disabled={busy[row.id]}
                        label={t("carriers.showInQuotes", { name })}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-ui-surface-sunken">
                      <td />
                      <td colSpan={3} className="px-3.5 pb-3.5 pt-1">
                        <ServiceChecklist
                          row={row}
                          onChange={(changes) => onServicesChange(row, changes)}
                          disabled={busy[row.id]}
                          inline
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarrierListView;
